import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Check, Loader2, ShieldCheck, Sparkles, Lock, Zap } from "lucide-react";

export default function Billing() {
  const { user, refresh, logout } = useAuth();
  const [params] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [subscribingPlan, setSubscribingPlan] = useState(null);
  const [plans, setPlans] = useState(null);
  const isPaywall = params.get("paywall") === "1" || params.get("welcome") === "1";
  const noAccess = user && user.has_access === false && user.role !== "admin";
  const currentPlan = user?.plan; // "pro" | "proplus" | undefined

  useEffect(() => {
    api.get("/billing/plans").then((r) => setPlans(r.data.plans)).catch(() => {});
  }, []);

  useEffect(() => {
    if (params.get("success") && params.get("mocked")) {
      toast.warning("Stripe en modo MOCKED — añade una clave Stripe real (sk_test_...) en backend/.env para activar pagos reales.");
    } else if (params.get("success")) {
      const plan = params.get("plan");
      toast.success(`¡Suscripción ${plan === "proplus" ? "PRO" : "Básico"} activada!`);
      refresh();
    }
    if (params.get("canceled")) toast("Pago cancelado");
    if (params.get("past_due")) toast.error("Pago rechazado — actualiza tu tarjeta");
  }, []); // eslint-disable-line

  const subscribe = async (planId) => {
    setSubscribingPlan(planId);
    try {
      const { data } = await api.post("/billing/checkout", { plan: planId });
      window.location.href = data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error iniciando checkout. Verifica que la clave de Stripe sea válida.");
      setSubscribingPlan(null);
    }
  };

  const cancel = async () => {
    if (!window.confirm("¿Seguro que quieres cancelar tu suscripción al final del periodo actual?")) return;
    setLoading(true);
    try {
      const { data } = await api.post("/billing/cancel");
      if (data.mocked) toast.warning("Cancelación simulada (Stripe en modo MOCKED)");
      else toast.success("Cancelación programada al final del periodo");
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error cancelando");
    } finally { setLoading(false); }
  };

  const resume = async () => {
    setLoading(true);
    try {
      await api.post("/billing/resume");
      toast.success("Suscripción reactivada");
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error reactivando");
    } finally { setLoading(false); }
  };

  const trialDays = user?.trial_ends_at
    ? Math.max(0, Math.ceil((new Date(user.trial_ends_at) - new Date()) / (1000 * 60 * 60 * 24)))
    : null;

  const isSubscribed = !user?.free_forever && ["active", "trialing", "past_due"].includes(user?.subscription_status);
  const showPlans = !user?.free_forever && (!isSubscribed || (isSubscribed && currentPlan !== "proplus"));

  return (
    <div data-testid="billing-page" className="max-w-5xl mx-auto">
      {/* Past-due banner */}
      {user?.subscription_status === "past_due" && (
        <div className="border-2 border-red-500 bg-red-50 p-5 mb-6" data-testid="past-due-banner">
          <div className="flex items-start gap-3">
            <div className="h-10 w-10 bg-red-600 text-white flex items-center justify-center flex-shrink-0">⚠️</div>
            <div className="flex-1">
              <div className="font-display font-extrabold text-lg text-red-900">Pago rechazado por tu banco</div>
              <p className="text-sm text-red-800 mt-1">
                Hemos intentado cobrar tu suscripción pero el banco rechazó el cargo. Stripe lo volverá a intentar automáticamente.
                Para evitar perder el acceso, actualiza tu tarjeta abajo.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Cancel scheduled banner */}
      {user?.cancel_at_period_end && !user?.free_forever && (
        <div className="border-2 border-yellow-500 bg-yellow-50 p-5 mb-6" data-testid="cancel-scheduled-banner">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <div className="font-display font-extrabold text-lg text-yellow-900">Cancelación programada</div>
              <p className="text-sm text-yellow-800 mt-1">
                Tu suscripción terminará al final del periodo actual. Después no se renovará y perderás el acceso.
              </p>
            </div>
            <button onClick={resume} disabled={loading} data-testid="btn-resume" className="h-10 px-4 bg-yellow-600 text-white font-bold text-sm hover:bg-yellow-700 disabled:opacity-60">
              Reactivar suscripción
            </button>
          </div>
        </div>
      )}

      {/* Welcome / paywall banner */}
      {isPaywall && noAccess && (
        <div className="border-2 border-orange-500 bg-orange-50 p-6 mb-8" data-testid="paywall-banner">
          <div className="flex items-start gap-4">
            <div className="h-12 w-12 bg-orange-600 text-white flex items-center justify-center flex-shrink-0">
              <Lock className="w-6 h-6" />
            </div>
            <div>
              <div className="kbar text-orange-700">{params.get("welcome") ? "Cuenta creada · 1 paso más" : "Suscripción requerida"}</div>
              <h2 className="font-display font-extrabold text-2xl sm:text-3xl mt-2">
                Activa tu cuenta con 14 días gratis
              </h2>
              <p className="text-slate-700 mt-2 text-sm sm:text-base">
                Elige el plan que mejor se adapta a ti.<br/>
                <span className="font-bold">Sin cargo durante 14 días</span> en ambos — puedes cancelar antes y no se cobra nada.
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="kbar">Suscripción</div>
      <h1 className="font-display font-extrabold text-3xl sm:text-5xl tracking-tight mt-2">Plan</h1>

      {/* Status */}
      <div className="mt-8 border border-slate-200 p-6 bg-white">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <div className="kbar">Estado actual</div>
            <div className="mt-2 text-2xl font-display font-extrabold capitalize" data-testid="sub-status">
              {user?.free_forever ? "Cuenta gratuita permanente" :
                user?.subscription_status === "active" ? "Activo" :
                user?.subscription_status === "trialing" ? "Periodo de prueba" :
                user?.subscription_status === "pending_payment" ? "Pendiente de pago" :
                user?.subscription_status === "canceled" ? "Cancelada" :
                user?.subscription_status}
            </div>
            {currentPlan && isSubscribed && (
              <div className="text-sm text-slate-500 mt-1">
                Plan: <span className="font-bold">{currentPlan === "proplus" ? "PRO" : "Básico"}</span>
              </div>
            )}
            {user?.subscription_status === "trialing" && trialDays !== null && (
              <div className="text-sm text-slate-500 mt-1">{trialDays} días restantes de prueba gratuita</div>
            )}
            {user?.free_forever && (
              <div className="text-sm text-green-700 mt-1 inline-flex items-center gap-1">
                <ShieldCheck className="w-4 h-4" /> Acceso de por vida sin pago
              </div>
            )}
          </div>
          {user?.free_forever ? (
            <span className="px-3 py-1 bg-green-100 text-green-700 text-xs font-bold uppercase tracking-wider">Gratis siempre</span>
          ) : user?.subscription_status === "active" ? (
            <span className="px-3 py-1 bg-green-100 text-green-700 text-xs font-bold uppercase tracking-wider">Pagando</span>
          ) : user?.subscription_status === "pending_payment" ? (
            <span className="px-3 py-1 bg-orange-100 text-orange-700 text-xs font-bold uppercase tracking-wider">Pendiente</span>
          ) : (
            <span className="px-3 py-1 bg-slate-100 text-slate-700 text-xs font-bold uppercase tracking-wider">{user?.subscription_status}</span>
          )}
        </div>
      </div>

      {/* Pricing cards */}
      {showPlans && plans && (
        <div className="mt-8" data-testid="pricing-cards">
          {isSubscribed && currentPlan === "pro" && (
            <div className="bg-orange-50 border border-orange-200 p-4 mb-4 text-sm">
              <span className="font-bold">¿Necesitas BC3, OCR, Verifactu o Contabilidad?</span> Sube a PRO por sólo 10 € más al mes.
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-slate-200 border border-slate-200">
            {plans.map((p) => (
              <PlanCard
                key={p.id}
                plan={p}
                isCurrent={isSubscribed && currentPlan === p.id}
                onSubscribe={() => subscribe(p.id)}
                loading={subscribingPlan === p.id}
                disabled={!!subscribingPlan}
              />
            ))}
          </div>
          <div className="text-xs text-slate-500 mt-3 flex items-center gap-1.5">
            🔒 Pago procesado por Stripe · 14 días gratis · Cancela cuando quieras
          </div>
        </div>
      )}

      {/* Manage subscription — visible when active/trialing */}
      {!user?.free_forever && (user?.subscription_status === "active" || user?.subscription_status === "trialing") && !user?.cancel_at_period_end && (
        <div className="mt-8 border border-slate-200 p-6 bg-white" data-testid="manage-section">
          <div className="kbar mb-2">Gestionar suscripción</div>
          <p className="text-sm text-slate-600 mb-4">
            Si cancelas, mantendrás el acceso hasta el final del periodo actual. No se cobrará el siguiente mes.
          </p>
          <button onClick={cancel} disabled={loading} data-testid="btn-cancel" className="h-11 px-5 border-2 border-red-200 hover:border-red-500 hover:text-red-700 text-sm font-bold">
            {loading ? "Cancelando…" : "Cancelar suscripción"}
          </button>
        </div>
      )}

      {/* Logout option for paywalled users */}
      {noAccess && (
        <div className="mt-8 text-center">
          <button onClick={logout} className="text-sm text-slate-500 hover:text-slate-900 underline" data-testid="btn-logout-paywall">
            Cerrar sesión
          </button>
        </div>
      )}
    </div>
  );
}

function PlanCard({ plan, isCurrent, onSubscribe, loading, disabled }) {
  const isPlus = plan.id === "proplus";
  return (
    <div
      data-testid={`plan-card-${plan.id}`}
      className={`p-7 ${isPlus ? "bg-slate-900 text-white" : "bg-white"}`}
    >
      <div className="flex items-baseline gap-2">
        <div className={`kbar ${isPlus ? "text-orange-400" : "text-orange-600"}`}>{plan.label}</div>
        {plan.highlight && (
          <span className="ml-auto px-2 py-0.5 bg-orange-600 text-white text-[10px] font-bold uppercase tracking-widest inline-flex items-center gap-1">
            <Zap className="w-3 h-3" /> Más completo
          </span>
        )}
        {isCurrent && (
          <span className="ml-auto px-2 py-0.5 bg-green-600 text-white text-[10px] font-bold uppercase tracking-widest">
            Tu plan
          </span>
        )}
      </div>
      <div className="mt-3 font-display font-extrabold text-5xl tabular-nums">
        {plan.price_eur} €<span className={`text-lg font-normal ml-1 ${isPlus ? "text-slate-400" : "text-slate-500"}`}>/mes</span>
      </div>
      <p className={`mt-3 text-sm ${isPlus ? "text-slate-300" : "text-slate-600"}`}>
        14 días gratis. Sin permanencia. Cancela en 1 clic.
      </p>
      <ul className="mt-6 space-y-2 text-sm">
        {plan.features.map((f) => (
          <li key={f} className="flex items-start gap-2">
            <Check className={`w-4 h-4 flex-shrink-0 mt-0.5 ${isPlus ? "text-orange-400" : "text-orange-600"}`} />
            <span>{f}</span>
          </li>
        ))}
        {(plan.extra || []).map((f) => (
          <li key={f} className={`flex items-start gap-2 font-semibold ${isPlus ? "text-orange-300" : "text-orange-700"}`}>
            <Sparkles className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <span>{f}</span>
          </li>
        ))}
      </ul>
      <button
        onClick={onSubscribe}
        disabled={disabled || isCurrent}
        data-testid={`btn-subscribe-${plan.id}`}
        className={`mt-8 px-6 inline-flex items-center justify-center gap-2 disabled:opacity-50 font-bold text-base h-12 w-full ${
          isPlus ? "bg-orange-600 text-white hover:bg-orange-700" : "bg-slate-900 text-white hover:bg-slate-800"
        }`}
      >
        {loading ? <><Loader2 className="w-5 h-5 animate-spin" /> Cargando…</> : isCurrent ? (
          "Plan actual"
        ) : (
          <><Sparkles className="w-5 h-5" /> Empezar 14 días gratis</>
        )}
      </button>
    </div>
  );
}
